import { productEvent } from "@shared/analytics/productEvents";
import { SolomonLogo } from "@shared/components/Brand/SolomonLogo";
import { FEATURE_FLAGS } from "@solomon-apps/config";
import type { WalletConnectState } from "@solomon-apps/shared";
import {
    truncateAddress,
    useWallet,
    useWalletModal,
    WalletConnectButton
} from "@solomon-apps/shared";
import { useGuardedNavigate, useUnsavedChanges } from "@solomon-apps/shared";
import {
    HamburgerIcon,
    SignatureIcon
} from "@solomon-labs/design/components/icons";
import {
    ButtonIcon,
    NavMenu,
    NavMenuItem,
    Text
} from "@solomon-labs/design/components/ui";
import { AnimatePresence, motion } from "motion/react";
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import type { MenuItem } from "@/components/Menu";
import { MobileMenu } from "@/components/Menu";
import { UsdvMigrationWalletMenu } from "@/components/UsdvMigrationWalletMenu";
import { useSelectedSquadsVault } from "@/hooks/yaas/retailAccountStore";
import { RetailAccountSelector } from "@/pages/Yaas/components/RetailAccountSelector";

// YaaS folded into USDv — the standalone nav entry and the `/yaas`
// route are both gone. The surface is reached at `/`.
const NAV_ITEMS: MenuItem[] = [
    { label: "USDv", value: "/" },
    // DRAFT — the coverage registry. Sits in the nav (rather than only
    // behind the in-page ghost row) so it stays reachable for someone
    // who has not enabled rewards yet, and as fast nav for someone who
    // has.
    //
    // Dropped when `FEATURE_FLAGS.venues` is off, in step with the
    // route registration in `app.tsx` — a nav item pointing at an
    // unregistered path would land the user on NotFoundPage.
    ...(FEATURE_FLAGS.venues ? [{ label: "Venues", value: "/venues" }] : []),
    { label: "Stats", value: "/stats" }
];

function findActiveValue(pathname: string): string | undefined {
    // Exact match first, then prefix match (skip "/" to avoid matching everything)
    const exact = NAV_ITEMS.find((item) => item.value === pathname);
    if (exact) return exact.value;
    const prefix = NAV_ITEMS.find(
        (item) => item.value !== "/" && pathname.startsWith(`${item.value}/`)
    );
    return prefix?.value;
}

/**
 * "Landing layout" — pages built on a 60/40 guilloche split that
 * extend edge-to-edge instead of sitting inside the standard
 * `max-w-5xl` content gutter. The nav follows suit on these routes:
 * full-width with `px-4` instead of the gutter.
 *
 * `/` always uses the landing layout now — the home dispatcher routes
 * every connected/disconnected branch through `USDvPage` or
 * `RetailYaasLandingPage`, both of which use the 60/40 split.
 *
 */
const LANDING_LAYOUT_PATHS = ["/"];

export function NavigationBar(): React.ReactElement {
    const location = useLocation();
    // Use the guarded navigate so route changes from the nav prompt
    // a discard-confirm modal if a form elsewhere has unsaved
    // changes (see UnsavedChangesProvider). `canNavigate` powers the
    // wallet menus' `onBeforeDisconnect` gate below.
    const navigate = useGuardedNavigate();
    const { canNavigate } = useUnsavedChanges();

    // Landing-layout routes render a 60/40 guilloche split that
    // extends edge-to-edge, so the nav drops its `max-w-5xl` gutter
    // and uses `px-4` instead. `/` was previously state-dependent
    // (only landing-layout when no balance) but the home dispatcher
    // now routes every state through landing-style surfaces, so the
    // path-only check covers it.
    const isLandingFullWidth = LANDING_LAYOUT_PATHS.includes(location.pathname);

    // Wallet state
    const { connected, connecting, publicKey, walletName } = useWallet();
    const {
        openModal,
        connectError,
        connectingStep,
        showModal: isConnectModalOpen
    } = useWalletModal();
    const walletButtonRef = useRef<HTMLDivElement>(null);
    const [isWalletDropdownOpen, setIsWalletDropdownOpen] = useState(false);
    const [isWalletDrawerOpen, setIsWalletDrawerOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [justConnected, setJustConnected] = useState(false);
    const prevConnectedRef = useRef(false);

    // Keep the wallet button lifted above the backdrop during the menu's
    // exit animation (not just while it's open).
    const [isWalletMenuClosing, setIsWalletMenuClosing] = useState(false);
    const walletMenuClosingTimerRef = useRef<ReturnType<
        typeof setTimeout
    > | null>(null);
    const prevWalletMenuOpenRef = useRef(false);
    useEffect(() => {
        const menuOpen = isWalletDropdownOpen || isWalletDrawerOpen;
        if (!menuOpen && prevWalletMenuOpenRef.current) {
            setIsWalletMenuClosing(true);
            if (walletMenuClosingTimerRef.current !== null) {
                clearTimeout(walletMenuClosingTimerRef.current);
            }
            walletMenuClosingTimerRef.current = setTimeout(() => {
                setIsWalletMenuClosing(false);
                walletMenuClosingTimerRef.current = null;
            }, 250);
        } else if (menuOpen) {
            if (walletMenuClosingTimerRef.current !== null) {
                clearTimeout(walletMenuClosingTimerRef.current);
                walletMenuClosingTimerRef.current = null;
            }
            setIsWalletMenuClosing(false);
        }
        prevWalletMenuOpenRef.current = menuOpen;
    }, [isWalletDropdownOpen, isWalletDrawerOpen]);

    const isWalletMenuVisible =
        isWalletDropdownOpen || isWalletDrawerOpen || isWalletMenuClosing;

    const activeValue = findActiveValue(location.pathname);
    const params = new URLSearchParams(location.search);
    const showRetailAccount =
        location.pathname === "/" &&
        !params.has("state") &&
        !params.has("simulate");

    // While the modal flow is active, mirror the modal's state machine so
    // the button respects the same min-dwell timings. Outside the flow,
    // trust the wallet adapter — access policy isn't surfaced on the
    // button (the modal owns the not-whitelisted UX).
    const isFullyConnected =
        connectingStep !== null ? connectingStep === "connected" : connected;

    // Fire the 800ms `justConnected` flash exactly when `isFullyConnected`
    // transitions false→true. When the modal is active this lines up with
    // the modal reaching its "Wallet Connected" step — so the nav flash
    // and the modal's final frame land together instead of the nav
    // winning by 500ms–1s.
    useEffect(() => {
        if (isFullyConnected && !prevConnectedRef.current) {
            setJustConnected(true);
            const timer = setTimeout(() => setJustConnected(false), 800);
            return (): void => {
                clearTimeout(timer);
            };
        }
        prevConnectedRef.current = isFullyConnected;
    }, [isFullyConnected]);

    const handleNavSelect = (value: string): void => {
        productEvent("navigation:selected", {
            entry_point: isMobileMenuOpen ? "mobile_menu" : "header",
            target: value
        });
        navigate(value);
    };

    // Close mobile menu on route change
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    const handleWalletClick = (): void => {
        if (connected) {
            if (window.innerWidth >= 1024) {
                setIsWalletDropdownOpen((prev) => !prev);
            } else {
                setIsWalletDrawerOpen((prev) => !prev);
            }
        } else {
            openModal("header");
        }
    };

    const walletButtonText = (): string => {
        if (connectingStep === "checking") return "Checking...";
        if (connectingStep === "connecting" || connecting)
            return walletName ? `Connecting ${walletName}...` : "Connecting...";
        if (isFullyConnected && publicKey)
            return truncateAddress(publicKey.toString(), 4, 4);
        return "Connect";
    };

    // Map wallet lifecycle to the shared button's state axis. While the
    // modal's state machine is active (`connectingStep !== null`) we mirror
    // its steps 1:1 so the nav button respects the same min-dwell timings
    // as the modal. Outside the flow we fall back to the raw wallet-adapter
    // state, which handles auto-reconnect and the post-close "just still
    // connected" view.
    const walletState: WalletConnectState =
        connectingStep === "connecting" || connectingStep === "checking"
            ? "connecting"
            : connectingStep === "not-whitelisted" ||
                connectingStep === "access-error"
              ? "error"
              : connectingStep === "connected"
                ? justConnected
                    ? "just-connected"
                    : "connected"
                : connecting
                  ? "connecting"
                  : isFullyConnected && justConnected
                    ? "just-connected"
                    : isFullyConnected
                      ? "connected"
                      : connectError
                        ? "error"
                        : "disconnected";

    // Multisig mode: a Squads vault is selected for the connected signer.
    // The vault gets its own box beside the wallet's and takes the
    // indicator, since it is the active account; the wallet box stays to
    // say which wallet signs. Only once the wallet is connected: while
    // connecting or in error the single box carries that state.
    const signerAddress = publicKey?.toString() ?? "";
    const selectedVault = useSelectedSquadsVault(
        connected ? signerAddress : ""
    );
    const walletConnected =
        walletState === "connected" || walletState === "just-connected";
    const activeVault = walletConnected ? selectedVault : null;
    const signerLabel = truncateAddress(signerAddress, 4, 4);

    return (
        <>
            <nav
                className={`relative flex items-center justify-between mx-auto py-3 md:py-4 w-full ${
                    isLandingFullWidth ? "px-4" : "max-w-5xl px-3 lg:px-0"
                }`}
            >
                {/* Logo — left */}
                <a
                    href="/"
                    onClick={(e) => {
                        e.preventDefault();
                        productEvent("navigation:selected", {
                            entry_point: "header",
                            target: "/",
                            control: "logo"
                        });
                        navigate("/");
                    }}
                >
                    <SolomonLogo
                        className="h-6 w-auto"
                        fill="rgb(var(--color-charcoal))"
                        title="Solomon"
                    />
                </a>

                {/* Desktop navigation — absolutely centered so it stays anchored
                    to the page midline regardless of logo/wallet widths. */}
                <div className="hidden md:block absolute left-1/2 -translate-x-1/2">
                    <NavMenu
                        value={activeValue ?? ""}
                        onValueChange={handleNavSelect}
                    >
                        {NAV_ITEMS.filter((item) => !item.mobileOnly).map(
                            (item) => (
                                <NavMenuItem
                                    key={item.value}
                                    value={item.value}
                                >
                                    {item.label}
                                </NavMenuItem>
                            )
                        )}
                    </NavMenu>
                </div>

                {/* Right-side group: mobile hamburger + wallet.
                    Hamburger is mobile-only; wallet is always visible. */}
                <div className="flex items-center gap-3">
                    <ButtonIcon
                        variant="transparent"
                        size="xl"
                        onClick={(): void => {
                            productEvent("navigation:menu_opened", {
                                entry_point: "mobile_menu"
                            });
                            setIsMobileMenuOpen(true);
                        }}
                        className="md:hidden p-1"
                        aria-label="Open menu"
                        icon={<HamburgerIcon />}
                    />

                    {/* Wallet button — lifted above the dialog overlay
                        (z-40) while the connect modal or the Account
                        dropdown/drawer is open so the trigger stays crisp
                        and its state animations are visible. */}
                    <div
                        className={`flex gap-2 items-center ${isWalletMenuVisible || isConnectModalOpen ? "relative z-[45]" : ""}`}
                        ref={walletButtonRef}
                    >
                        {/* The active multisig first (outline, with the
                            indicator), then the signer's own wallet box.
                            Below md the pair does not fit beside the logo and
                            hamburger, so only the multisig box shows. */}
                        <AnimatePresence initial={false}>
                            {activeVault && (
                                <motion.div
                                    key="active-multisig"
                                    className="flex items-center gap-2"
                                    initial={{ opacity: 0, x: -4 }}
                                    animate={{
                                        opacity: 1,
                                        x: 0,
                                        transition: {
                                            duration: 0.15,
                                            ease: "easeOut"
                                        }
                                    }}
                                    exit={{
                                        opacity: 0,
                                        x: -4,
                                        transition: {
                                            duration: 0.1,
                                            ease: "easeIn"
                                        }
                                    }}
                                >
                                    {/* Names the box; desktop only, the
                                        mobile nav has no room for it. */}
                                    <Text
                                        variant="small"
                                        aria-hidden="true"
                                        className="hidden text-foreground-muted md:inline"
                                    >
                                        Multisig
                                    </Text>
                                    <WalletConnectButton
                                        state={walletState}
                                        label={truncateAddress(
                                            activeVault,
                                            4,
                                            4
                                        )}
                                        onClick={handleWalletClick}
                                        // Outline look, lifted like the wallet box: the same
                                        // sunken recipe with its face filled in the
                                        // nav's own ground, so it reads as
                                        // outline-only at rest.
                                        className="[&_[data-slot=button-face]]:bg-surface-ground"
                                        aria-label={`Active multisig ${truncateAddress(activeVault, 4, 4)}`}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                        <div
                            className={activeVault ? "hidden md:flex" : "flex"}
                        >
                            <WalletConnectButton
                                state={walletState}
                                label={walletButtonText()}
                                onClick={handleWalletClick}
                                indicator={!activeVault}
                                // The signer: a signature mark where the
                                // indicator was (that moved to the multisig).
                                leading={
                                    activeVault ? (
                                        <SignatureIcon
                                            aria-hidden="true"
                                            className="size-5"
                                        />
                                    ) : undefined
                                }
                                aria-label={
                                    activeVault
                                        ? `Wallet ${signerLabel}`
                                        : undefined
                                }
                            />
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile Navigation Menu */}
            <MobileMenu
                items={NAV_ITEMS}
                activeValue={activeValue}
                onSelect={handleNavSelect}
                isOpen={isMobileMenuOpen}
                onClose={() => {
                    productEvent("navigation:menu_closed", {
                        entry_point: "mobile_menu",
                        close_method: "dismiss"
                    });
                    setIsMobileMenuOpen(false);
                }}
            />

            <UsdvMigrationWalletMenu
                isWalletDropdownOpen={isWalletDropdownOpen}
                isWalletDrawerOpen={isWalletDrawerOpen}
                onCloseDropdown={() => setIsWalletDropdownOpen(false)}
                onCloseDrawer={() => setIsWalletDrawerOpen(false)}
                anchorEl={walletButtonRef.current}
                onBeforeDisconnect={canNavigate}
                renderAccountContent={
                    showRetailAccount
                        ? (
                              personalContent,
                              renderWalletRow
                          ): React.ReactNode => (
                              <RetailAccountSelector
                                  key={publicKey?.toString()}
                                  renderWalletRow={renderWalletRow}
                                  isOpen={
                                      isWalletDropdownOpen || isWalletDrawerOpen
                                  }
                              >
                                  {personalContent}
                              </RetailAccountSelector>
                          )
                        : undefined
                }
            />
        </>
    );
}
